import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Activity,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  CreditCard,
  FileText,
  Users,
  Package,
  ArrowRightLeft,
  CheckCircle,
  XCircle,
  DollarSign,
  Calendar,
  User,
  Handshake,
  Receipt,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';

type ActionCategory =
  | 'booking'
  | 'passenger'
  | 'item'
  | 'payment'
  | 'invoice'
  | 'plan'
  | 'commission'
  | 'expense'
  | 'allocation';

const actionConfig: Record<
  string,
  { icon: typeof Activity; color: string; category: ActionCategory }
> = {
  'booking.created': { icon: Plus, color: 'bg-green-100 text-green-700', category: 'booking' },
  'booking.updated': { icon: Pencil, color: 'bg-blue-100 text-blue-700', category: 'booking' },
  'booking.status_changed': {
    icon: ArrowRightLeft,
    color: 'bg-purple-100 text-purple-700',
    category: 'booking',
  },
  'passenger.added': { icon: Users, color: 'bg-green-100 text-green-700', category: 'passenger' },
  'passenger.updated': { icon: Pencil, color: 'bg-blue-100 text-blue-700', category: 'passenger' },
  'passenger.removed': { icon: Trash2, color: 'bg-red-100 text-red-700', category: 'passenger' },
  'item.added': { icon: Package, color: 'bg-green-100 text-green-700', category: 'item' },
  'item.updated': { icon: Pencil, color: 'bg-blue-100 text-blue-700', category: 'item' },
  'item.removed': { icon: Trash2, color: 'bg-red-100 text-red-700', category: 'item' },
  'item.cost_recorded': {
    icon: DollarSign,
    color: 'bg-orange-100 text-orange-700',
    category: 'item',
  },
  'item.supplier_paid': {
    icon: DollarSign,
    color: 'bg-yellow-100 text-yellow-700',
    category: 'item',
  },
  'payment.recorded': {
    icon: CreditCard,
    color: 'bg-green-100 text-green-700',
    category: 'payment',
  },
  'payment.deleted': { icon: Trash2, color: 'bg-red-100 text-red-700', category: 'payment' },
  'invoice.created': { icon: FileText, color: 'bg-green-100 text-green-700', category: 'invoice' },
  'invoice.updated': { icon: Pencil, color: 'bg-blue-100 text-blue-700', category: 'invoice' },
  'invoice.status_changed': {
    icon: ArrowRightLeft,
    color: 'bg-purple-100 text-purple-700',
    category: 'invoice',
  },
  'invoice.payment_recorded': {
    icon: CreditCard,
    color: 'bg-green-100 text-green-700',
    category: 'invoice',
  },
  'invoice.item_added': { icon: Plus, color: 'bg-green-100 text-green-700', category: 'invoice' },
  'invoice.item_removed': { icon: Trash2, color: 'bg-red-100 text-red-700', category: 'invoice' },
  'plan.created': { icon: Calendar, color: 'bg-green-100 text-green-700', category: 'plan' },
  'plan.installment_updated': {
    icon: Pencil,
    color: 'bg-blue-100 text-blue-700',
    category: 'plan',
  },
  'plan.installment_paid': {
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    category: 'plan',
  },
  'plan.cancelled': { icon: XCircle, color: 'bg-red-100 text-red-700', category: 'plan' },
  'commission.created': {
    icon: Handshake,
    color: 'bg-green-100 text-green-700',
    category: 'commission',
  },
  'commission.calculated': {
    icon: Handshake,
    color: 'bg-blue-100 text-blue-700',
    category: 'commission',
  },
  'commission.approved': {
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    category: 'commission',
  },
  'commission.paid': {
    icon: DollarSign,
    color: 'bg-green-100 text-green-700',
    category: 'commission',
  },
  'commission.cancelled': {
    icon: XCircle,
    color: 'bg-red-100 text-red-700',
    category: 'commission',
  },
  'expense.created': { icon: Receipt, color: 'bg-green-100 text-green-700', category: 'expense' },
  'expense.updated': { icon: Pencil, color: 'bg-blue-100 text-blue-700', category: 'expense' },
  'expense.approved': {
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    category: 'expense',
  },
  'expense.rejected': { icon: XCircle, color: 'bg-red-100 text-red-700', category: 'expense' },
  'expense.paid': { icon: DollarSign, color: 'bg-green-100 text-green-700', category: 'expense' },
  'expense.linked': {
    icon: ArrowRightLeft,
    color: 'bg-blue-100 text-blue-700',
    category: 'expense',
  },
  'allocation.consumed': {
    icon: Shield,
    color: 'bg-purple-100 text-purple-700',
    category: 'allocation',
  },
  'allocation.released': {
    icon: Shield,
    color: 'bg-orange-100 text-orange-700',
    category: 'allocation',
  },
};

const categories: ActionCategory[] = [
  'booking',
  'passenger',
  'item',
  'payment',
  'invoice',
  'plan',
  'commission',
  'expense',
  'allocation',
];

function formatTimeAgo(
  date: Date,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return t('timeAgo.justNow');
  if (diffMin < 60) return t('timeAgo.minutes', { count: diffMin });
  if (diffHour < 24) return t('timeAgo.hours', { count: diffHour });
  if (diffDay < 7) return t('timeAgo.days', { count: diffDay });
  return new Date(date).toLocaleDateString();
}

export function ActivityFeedPage() {
  const { t } = useTranslation('activityFeed');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useSessionState<string>('travel:activity-feed:search', '');
  const [searchInput, setSearchInput] = useState(search);
  const [selectedCategory, setSelectedCategory] = useSessionState<ActionCategory | ''>(
    'travel:activity-feed:selectedCategory',
    ''
  );
  const [startDate, setStartDate] = useSessionState<string>('travel:activity-feed:startDate', '');
  const [endDate, setEndDate] = useSessionState<string>('travel:activity-feed:endDate', '');

  // Map category to entityType filter
  const entityTypeMap: Record<string, string> = {
    booking: 'Booking',
    passenger: 'Passenger',
    item: 'BookingItem',
    payment: 'Payment',
    invoice: 'Invoice',
    plan: 'PaymentPlan',
    commission: 'Commission',
    expense: 'Expense',
    allocation: 'Allocation',
  };

  const { data, isLoading } = trpc.activityFeed.list.useQuery({
    page,
    search: search || undefined,
    entityType: selectedCategory ? entityTypeMap[selectedCategory] : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Activity className="h-6 w-6" />
          {t('title')}
        </h1>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="border-border flex flex-wrap items-end gap-4 border bg-white p-4">
        {/* Search */}
        <div className="min-w-[200px] flex-1">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-2">
          <Filter className="text-muted-foreground h-4 w-4" />
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => {
                setSelectedCategory('');
                setPage(1);
              }}
              className={`border px-2.5 py-1 text-xs font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t('filters.all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCategory(cat === selectedCategory ? '' : cat);
                  setPage(1);
                }}
                className={`border px-2.5 py-1 text-xs font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(`categories.${cat}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Date filters */}
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            className="h-8 w-36 text-xs"
          />
          <span className="text-muted-foreground text-xs">—</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            className="h-8 w-36 text-xs"
          />
        </div>

        {/* Clear */}
        {(search || selectedCategory || startDate || endDate) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('');
              setSearchInput('');
              setSelectedCategory('');
              setStartDate('');
              setEndDate('');
              setPage(1);
            }}
          >
            {t('filters.clear')}
          </Button>
        )}
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="border-border border bg-white p-12 text-center">
          <Activity className="text-muted-foreground/30 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('empty.title')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">{t('empty.description')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((item) => {
            const config = actionConfig[item.action] || {
              icon: Activity,
              color: 'bg-gray-100 text-gray-700',
              category: 'booking' as ActionCategory,
            };
            const Icon = config.icon;
            const details = item.details || {};
            const bookingId = details.bookingId as string | undefined;
            const bookingRef = details.bookingReference as string | undefined;
            const customerName = details.customerName as string | undefined;
            const partnerName = details.partnerName as string | undefined;
            const description = details.description as string | undefined;
            const amount = details.amount as number | undefined;
            const currency = details.currency as string | undefined;

            return (
              <div
                key={item.id}
                className="border-border hover:bg-muted/30 border bg-white p-4 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center ${config.color}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    {/* Action + time */}
                    <div className="flex items-center justify-between gap-2">
                      <span className={`px-2 py-0.5 text-[11px] font-medium ${config.color}`}>
                        {t(`actions.${item.action}`, item.action)}
                      </span>
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {formatTimeAgo(item.createdAt, t)}
                      </span>
                    </div>

                    {/* Description */}
                    {description && <p className="mt-1 text-sm">{description}</p>}

                    {/* Metadata row */}
                    <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      {/* Booking link */}
                      {bookingId && bookingRef && (
                        <Link
                          to={`/travel/bookings/${bookingId}`}
                          className="text-primary flex items-center gap-1 font-mono hover:underline"
                        >
                          <Package className="h-3 w-3" />
                          {bookingRef}
                        </Link>
                      )}

                      {/* User */}
                      {item.user && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {item.user.name}
                        </span>
                      )}

                      {/* Customer */}
                      {customerName && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {customerName}
                        </span>
                      )}

                      {/* Partner */}
                      {partnerName && (
                        <span className="flex items-center gap-1">
                          <Handshake className="h-3 w-3" />
                          {partnerName}
                        </span>
                      )}

                      {/* Amount */}
                      {amount !== undefined && currency && (
                        <span className="flex items-center gap-1 font-mono">
                          <DollarSign className="h-3 w-3" />
                          {currency} {amount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="border-border flex items-center justify-between border bg-white px-4 py-3">
              <span className="text-muted-foreground text-sm">
                {t('pagination.showing', {
                  from: (page - 1) * 30 + 1,
                  to: Math.min(page * 30, data.pagination.total),
                  total: data.pagination.total,
                })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">
                  {page} / {data.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= data.pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
